/**
 * B3 consumer flow — guest session + attribution helpers.
 * Isolated from B2B WizardProvider on purpose (B3 wiring plan §"What B3 does NOT touch").
 *
 * Guest token is the ONLY auth artifact until S3 graduation. Persisted per-program-slug
 * so switching programs doesn't cross-contaminate sessions.
 */
import { supabase } from "@/integrations/supabase/client";

const TOKEN_KEY = (slug: string) => `cmoji.consumer.guest.${slug}`;
const ATTR_KEY = (slug: string) => `cmoji.consumer.attr.${slug}`;
const SELFIE_KEY = (slug: string) => `cmoji.consumer.selfie.${slug}`;
const STYLE_KEY = (slug: string) => `cmoji.consumer.style.${slug}`;

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  partner?: string;
  campaign?: string;
  ref?: string;
  landed_at?: string;
}

export interface StylePick {
  style_id: string;
  sub_style_id?: string | null;
  hobby_id?: string | null;
}

export function captureAttributionFromLocation(search: string): Attribution {
  const p = new URLSearchParams(search);
  const attr: Attribution = { landed_at: new Date().toISOString() };
  const keys: (keyof Attribution)[] = [
    "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
    "partner", "campaign", "ref",
  ];
  for (const k of keys) {
    const v = p.get(k as string);
    if (v) (attr as any)[k] = v.slice(0, 128);
  }
  return attr;
}

export function saveAttribution(slug: string, attr: Attribution) {
  try {
    const existing = readAttribution(slug);
    // First-touch wins for utm_*, but always update landed_at.
    const merged = { ...attr, ...existing, landed_at: attr.landed_at ?? existing.landed_at };
    localStorage.setItem(ATTR_KEY(slug), JSON.stringify(merged));
  } catch {}
}

export function readAttribution(slug: string): Attribution {
  try {
    const raw = localStorage.getItem(ATTR_KEY(slug));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

export function readGuestToken(slug: string): string | null {
  try { return localStorage.getItem(TOKEN_KEY(slug)); } catch { return null; }
}

function saveGuestToken(slug: string, token: string) {
  try { localStorage.setItem(TOKEN_KEY(slug), token); } catch {}
}

export function clearGuestToken(slug: string) {
  try { localStorage.removeItem(TOKEN_KEY(slug)); } catch {}
}

/** Idempotent bootstrap. Returns the guest token, creating a session if needed. */
export async function ensureGuestSession(slug: string, attribution: Attribution): Promise<string> {
  const existing = readGuestToken(slug);
  if (existing) return existing;
  const { data, error } = await supabase.rpc("create_guest_session", {
    p_attribution: attribution as any,
  });
  if (error) throw error;
  const token = (data as any)?.token;
  if (!token) throw new Error("guest_session_missing_token");
  saveGuestToken(slug, token);
  return token;
}

export function readStylePick(slug: string): StylePick | null {
  try {
    const raw = localStorage.getItem(STYLE_KEY(slug));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function saveStylePick(slug: string, pick: StylePick) {
  try { localStorage.setItem(STYLE_KEY(slug), JSON.stringify(pick)); } catch {}
}

export function readSelfiePath(slug: string): string | null {
  try { return localStorage.getItem(SELFIE_KEY(slug)); } catch { return null; }
}

export function saveSelfiePath(slug: string, path: string) {
  try { localStorage.setItem(SELFIE_KEY(slug), path); } catch {}
}

export function clearSelfiePath(slug: string) {
  try { localStorage.removeItem(SELFIE_KEY(slug)); } catch {}
}

/* ── B3 Group 3: variant selection + details staging ── */

const VARIANT_KEY = (slug: string) => `cmoji.consumer.variant.${slug}`;
const DETAILS_KEY = (slug: string) => `cmoji.consumer.details.${slug}`;

export interface SelectedVariant {
  generation_id: string;
  image_url: string;
  variant_number?: number;
  /** Wizard-parity card presentation, chosen on S5. */
  orientation?: "portrait" | "landscape";
  card_color?: string;
  avatar_scale?: number;
  avatar_position?: { x: number; y: number };
}

export function saveSelectedVariant(slug: string, v: SelectedVariant) {
  try { localStorage.setItem(VARIANT_KEY(slug), JSON.stringify(v)); } catch {}
}

export function readSelectedVariant(slug: string): SelectedVariant | null {
  try {
    const raw = localStorage.getItem(VARIANT_KEY(slug));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/** Drop the staged KYC/details draft (used when a NEW order starts). */
export function clearDetails(slug: string) {
  try { localStorage.removeItem(DETAILS_KEY(slug)); } catch {}
}

export interface ConsumerDetails {
  first_name: string;
  /** Optional. LOAD-BEARING: S6 prints the middle INITIAL on the card face. */
  middle_name?: string | null;
  last_name: string;
  /** Contact number for issuer fulfilment. Digits only once committed. */
  phone?: string | null;
  /**
   * KYC/OFAC IDENTITY VERIFICATION ONLY, as `YYYY-MM-DD`.
   *
   * PURPOSE-BOUND: this must never be used to derive age or to satisfy age
   * screening. Age screening is the S3 affirmation tickbox + the `age` consent
   * record — a different purpose and a different lawful basis. Two purposes,
   * two mechanisms. Do not add an age comparison against this field.
   */
  date_of_birth?: string | null;
  delivery_type: "in_branch" | "mail";
  branch_id?: string | null;
  postal_address?: {
    line1?: string; line2?: string; city?: string; region?: string; postcode?: string; country?: string;
  } | null;
  pan_last5?: string | null;
  card_color?: string;
  orientation?: "landscape" | "portrait";
  /**
   * CAPTURED PREFERENCE ONLY — what the customer intends their ISSUING BANK to
   * load onto the card after issuance. Never part of the Cardmoji fee/total.
   */
  intended_load_amount_cents?: number | null;
  /**
   * HOW MANY CARDS this one order is for. It exists ONLY to pick a CONFIGURED
   * price tier — the flow never multiplies a single fee by it, and the server
   * re-resolves the amount from `pricingTiers` and refuses a quantity the
   * PaymentIntent was not created for. Absent/invalid = 1.
   */
  card_quantity?: number | null;
}


export function saveDetails(slug: string, d: ConsumerDetails) {
  try { localStorage.setItem(DETAILS_KEY(slug), JSON.stringify(d)); } catch {}
}

export function readDetails(slug: string): ConsumerDetails | null {
  try {
    const raw = localStorage.getItem(DETAILS_KEY(slug));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/* ── S5 Reveal: card customisation staging (colourway/orientation/size/share) ── */

const CUSTOM_KEY = (slug: string) => `cmoji.consumer.customisation.${slug}`;

export interface CardCustomization {
  colour: string;
  cname: string;
  orientation: "portrait" | "landscape";
  size: number;
  /** Wizard-parity avatar framing, as percentages (50/50 = centred). */
  pos_x?: number;
  pos_y?: number;
  format: "story" | "post";
  caption: string;
  /**
   * LEGACY ONLY. Browser-minted referral codes are gone: nothing writes this
   * any more, and no surface may present it as a redeemable code. Kept optional
   * so previously stored sessions still parse.
   */
  referral_code?: string;
}


export function saveCardCustomization(slug: string, c: CardCustomization) {
  try { localStorage.setItem(CUSTOM_KEY(slug), JSON.stringify(c)); } catch {}
}

export function readCardCustomization(slug: string): CardCustomization | null {
  try {
    const raw = localStorage.getItem(CUSTOM_KEY(slug));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearCardCustomization(slug: string) {
  try { localStorage.removeItem(CUSTOM_KEY(slug)); } catch {}
}

/* ── §1 ANALYTICS · funnel state stream ───────────────────────────────
 * Every step a visitor reaches is recorded against their guest session so
 * drop-off can be measured BEFORE a card_request row exists. Fire-and-forget:
 * a failed metric must never block the funnel.
 */

/**
 * The ALLOWED keys are the `funnel_states` rows in the database — that table is
 * the authority and the events table has a foreign key to it. This union exists
 * so a typo fails at compile time; it must stay in step with those rows.
 */
export type FunnelState =
  | "landed"
  | "style_picked"
  | "arrived_selfie"
  | "selfie_uploaded"
  | "arrived_same_face"
  | "arrived_consent"
  | "consented"
  | "account_created"
  | "generating"
  | "revealed"
  | "arrived_share"
  | "arrived_name"
  | "arrived_phone"
  | "arrived_dob"
  | "arrived_address"
  | "arrived_fee"
  | "details"
  | "arrived_load"
  | "arrived_pay"
  | "arrived_authorise"
  | "payment_authorised"
  | "arrived_confirmed"
  | "submitted"
  /* Field completions — recorded where the field gates the button. */
  | "field_name_complete"
  | "field_phone_complete"
  | "field_dob_complete"
  | "field_address_line1"
  | "field_address_city"
  | "field_address_region"
  | "field_address_postcode"
  | "field_address_complete"
  /* Drop-off CAUSES, not steps. */
  | "generation_failed"
  | "payment_declined"
  | "three_ds_abandoned"
  | "code_not_entered"
  /* What we SENT for checking: length + repeat only, never the code itself. */
  | "code_submitted"
  | "stepped_back";

export function recordFunnelEvent(
  slug: string,
  state: FunnelState,
  programId?: string | null,
  meta?: Record<string, unknown>,
): void {
  void recordFunnelEventDurably(slug, state, programId, meta);
}

/** Wait until evidence is stored before a navigation or provider call can cancel it. */
export async function recordFunnelEventDurably(
  slug: string,
  state: FunnelState,
  programId?: string | null,
  meta?: Record<string, unknown>,
): Promise<void> {
  // After graduation the guest token is gone; the RPC then resolves the session
  // from the signed-in account, so a missing token is NOT a reason to skip.
  const token = readGuestToken(slug) ?? "";
  try {
    await supabase.rpc("record_funnel_event", {
      p_token: token,
      p_state: state,
      p_program_id: programId ?? null,
      p_meta: (meta ?? {}) as never,
    });
  } catch {
    /* metrics are never load-bearing */
  }
}

/* ── Age gate (REJECT-only) marker ────────────────────────────────────
 * Records ONLY the decision for this browser: "passed" or "blocked".
 * The date of birth is never stored — here or on the server.
 */

const AGE_GATE_KEY = (slug: string) => `cmoji.consumer.agegate.${slug}`;

export type AgeGateMark = "passed" | "blocked";

export function readAgeGateMark(slug: string): AgeGateMark | null {
  try {
    const v = localStorage.getItem(AGE_GATE_KEY(slug));
    return v === "passed" || v === "blocked" ? v : null;
  } catch { return null; }
}

export function saveAgeGateMark(slug: string, mark: AgeGateMark) {
  try { localStorage.setItem(AGE_GATE_KEY(slug), mark); } catch {}
}
